import type { AxiosError } from 'axios';

/**
 * Mensaje legible para el usuario a partir de errores Axios / fetch.
 * Evita mostrar el genérico inglés "Network Error" sin contexto.
 */
export function getUserFacingApiMessage(err: unknown): string {
  if (err === null || err === undefined) {
    return '';
  }

  if (typeof err === 'object' && 'message' in err && typeof (err as Error).message === 'string') {
    const msg = (err as Error).message;
    if (msg === 'No se pudo obtener el token CSRF. Por favor, recarga la página.') {
      return msg;
    }
  }

  const ax = err as AxiosError<{ detail?: unknown }>;
  const status = ax.response?.status;
  const code = ax.code;

  if (!ax.response) {
    if (code === 'ECONNABORTED') {
      return 'La petición ha superado el tiempo de espera (timeout). Inténtelo de nuevo o recargue la página.';
    }
    if (code === 'ERR_NETWORK' || ax.message === 'Network Error') {
      return (
        'No hay respuesta del servidor (red o cortafuegos). Comprueba la conexión, que el backend esté ' +
        'disponible y recarga la página si el problema continúa. Si acaba de guardarse el valor, puede ' +
        'haberse aplicado igualmente en el servidor.'
      );
    }
    if (ax.message && ax.message !== 'Network Error') {
      return ax.message;
    }
    return (
      'Error de comunicación con el servidor. Verifique la red y recargue si es necesario.'
    );
  }

  const d = ax.response.data?.detail;
  if (typeof d === 'string' && d.trim()) {
    return d;
  }
  if (Array.isArray(d)) {
    const parts = d
      .map((x: unknown) =>
        typeof x === 'object' && x !== null && 'msg' in x ? String((x as { msg?: string }).msg) : ''
      )
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
  }

  if (status === 403) {
    return 'Acceso denegado (403). Puede ser token CSRF o permisos: recargue la página e inténtelo de nuevo.';
  }
  if (status === 401) {
    return 'Sesión no válida (401). Inicie sesión de nuevo.';
  }
  if (status === 404) {
    return 'Recurso no encontrado (404). El ajuste puede haber sido sustituido; pulse «Reajustar timestamps».';
  }
  if (status && status >= 500) {
    return `Error del servidor (${status}). Inténtelo más tarde o contacte con soporte.`;
  }

  return ax.message || `Error HTTP ${status ?? ''}`.trim();
}
